import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ZodFormattedError } from 'zod';
import { ButtonAttributes } from '@celebration/design-system/dist/lib/button-group/props.types';
import { ClbModal } from '@celebration/react/ClbModal/ClbModal';
import { ClbLoading } from '@celebration/react/ClbLoading/ClbLoading';
import { useTranslation } from 'react-i18next';
import {
    ButtonPropsModal,
    ItemsFooterType,
} from '@celebration/react/ClbModal/ClbModal.types';
import IUserEditFormFullPageFlowProps from './interfaces/IUserEditFormFullPageFlowProps';
import IUserEditFormDataErrors from './interfaces/IUserEditFormDataErrors';
import { useToast } from '../../../../app/contexts/toast/toast.context';
import IUserFormData from '../user-form/interfaces/IUserFormData';
import UserFormSteps from '../user-form/enums/user-form-steps';
import IHandleUserFormChangeProps from '../user-form/interfaces/IHandleUserChangeProps';
import FullPageFlow from '../../../../common/components/full-page-flow';
import UserOverview from '../../../../common/components/users/overview';
import UserEditFormInfos from './components/user-edit-form-infos/user-edit-form-infos';
import UserEditFormService from './services/user-edit-form.service';
import UserEditFormValidationService from './services/user-edit-form.validation.service';
import UserAccountType from './enums/user-account-type.enum';
import Auth from '../../../../app/contexts/auth';
import InternalUserTypes from './enums/internal-user-types.enum';
import ThirdUserTypes from './enums/third-user-types.enum';

function UserEditFormFullPageFlow({
    isUserEditFormOpened,
    setIsUserEditFormOpened,
    setEditUserId,
    userId,
    setGetUsersData,
}: Readonly<IUserEditFormFullPageFlowProps>) {
    const { t } = useTranslation();
    const { user, isThirdUser } = Auth.useAuth();

    const generalInfoValidatedOnce = useRef(false);

    const emptyErrors = useMemo(
        () => ({}) as ZodFormattedError<IUserEditFormDataErrors>,
        [],
    );

    const [errors, setErrors] = useState(emptyErrors);

    const { setToast } = useToast();

    const [isLoading, setIsLoading] = useState<boolean>();

    const [userAccountType, setUserAccountType] = useState<UserAccountType>(
        UserAccountType.internal,
    );

    const [isExitModalOpen, setIsExitModalOpen] = useState(false);

    const [
        isShowingUserFormConfirmationModal,
        setIsShowingUserFormConfirmationModal,
    ] = useState(false);

    const [currentUserData, setCurrentUserData] = useState<
        IUserFormData | undefined
    >();

    const [activeStep, setActiveStep] = useState<UserFormSteps>(
        UserFormSteps.info,
    );

    const emptyUserEditData = useMemo(
        () =>
            ({
                type: '0',
                userCarrier: isThirdUser()
                    ? {
                          carrierId: user?.carrier?.id,
                          name: user?.carrier?.name,
                          sulaHubId: user?.carrier?.sulahub_id,
                      }
                    : {
                          carrierId: '',
                      },
                systems: [
                    {
                        id: 'ontime-leilao',
                        label: 'OnTime Leilão',
                        number: 0,
                        isChecked: false,
                    },
                    {
                        id: 'smart-cargas',
                        label: 'Smart Cargas',
                        number: 1,
                        isChecked: false,
                    },
                ],
            }) as IUserFormData,
        [isThirdUser, user?.carrier],
    );

    const [userData, setUserData] = useState<IUserFormData>(emptyUserEditData);

    const validateInfos = useCallback((userData: IUserFormData) => {
        const infoResult =
            UserEditFormValidationService.validateInfosThirdUser(userData);

        if (!infoResult || !infoResult.success) {
            setErrors(
                infoResult.errors as ZodFormattedError<IUserEditFormDataErrors>,
            );
            return false;
        }

        return true;
    }, []);

    const revalidateInfos = useCallback(
        (userData: IUserFormData) => {
            if (generalInfoValidatedOnce.current) {
                if (!validateInfos(userData)) return;

                setErrors(emptyErrors);
            }
        },
        [validateInfos, emptyErrors],
    );

    const validateUserEditFormInfoStep = useCallback(
        async (userData: IUserFormData) => {
            generalInfoValidatedOnce.current = true;

            if (!validateInfos(userData)) {
                return false;
            }

            setErrors(emptyErrors);

            return true;
        },
        [emptyErrors, validateInfos],
    );

    const closeUserEditFormHandler = useCallback(
        () => setIsExitModalOpen(true),
        [],
    );

    const closeExitModalHandler = useCallback(
        () => setIsExitModalOpen(false),
        [],
    );

    const showUserEditFormConfirmationModal = useCallback(() => {
        setIsShowingUserFormConfirmationModal(true);
    }, []);

    const goToPreviousStepHandler = useCallback(
        () => setActiveStep(activeStep - 1),
        [activeStep],
    );

    const goToReviewStepHandler = useCallback(async () => {
        setIsLoading(true);
        let validInfo = true;
        if (
            !UserEditFormValidationService.checkIfInternalUser(userData.email)
        ) {
            validInfo = await validateUserEditFormInfoStep(userData);
        }

        setIsLoading(false);

        if (validInfo) {
            setActiveStep(UserFormSteps.review);
        }
    }, [userData, validateUserEditFormInfoStep]);

    const closeModalHandler = useCallback(() => {
        setIsExitModalOpen(false);
        setIsUserEditFormOpened(false);
        setActiveStep(UserFormSteps.info);
        setUserData(emptyUserEditData);
        setErrors(emptyErrors);
        setCurrentUserData(undefined);
        setEditUserId(undefined);
        generalInfoValidatedOnce.current = false;
    }, [
        emptyErrors,
        emptyUserEditData,
        setEditUserId,
        setIsUserEditFormOpened,
    ]);

    const closeUserFormConfirmationModal = useCallback(() => {
        setIsShowingUserFormConfirmationModal(false);
    }, []);

    const fetchUserData = useCallback(
        async (userId: string) => {
            try {
                const user = await UserEditFormService.getUserById(userId, t);

                const isInternalUser =
                    UserEditFormValidationService.checkIfInternalUser(
                        user.email,
                    );

                if (user.type === null) {
                    user.type = isInternalUser
                        ? InternalUserTypes.solicitanteUser.toString()
                        : ThirdUserTypes.carrierOperator.toString();
                }

                if (isThirdUser()) {
                    user.userCarrier = emptyUserEditData.userCarrier;
                }

                setCurrentUserData(user);
                setUserData(user);

                if (isInternalUser) {
                    setUserAccountType(UserAccountType.internal);
                } else {
                    setUserAccountType(UserAccountType.third);
                }
            } catch (error: any) {
                setToast('negative', t('Erro'), (error as Error).message);
            } finally {
                setIsLoading(false);
            }
        },
        [setToast, emptyUserEditData.userCarrier, isThirdUser, t],
    );

    useEffect(() => {
        if (currentUserData === undefined && userId) {
            setIsLoading(true);
            fetchUserData(userId);
        }
    }, [currentUserData, fetchUserData, userId]);

    const pageTitle = useMemo(() => {
        const step_title = t('Editar');

        switch (activeStep) {
            case UserFormSteps.info:
                return `${step_title} ${t('UsuarioEtapa1')}`;
            case UserFormSteps.review:
                return `${step_title}  ${t('UsuarioEtapa2')}`;
        }
    }, [activeStep, t]);

    const headerButtonList: ButtonAttributes[] = useMemo(() => {
        const headerEditFormButtons: ButtonAttributes[] = [];

        if (activeStep === UserFormSteps.info) {
            headerEditFormButtons.push({
                id: 'close-button',
                label: t('Fechar'),
                disabled: false,
                loading: false,
                title: t('UsuarioFecharEdicao'),
                ariaLabel: t('UsuarioFecharEdicao'),
                dataTestid: 'close-button',
                callback: closeUserEditFormHandler,
            });

            headerEditFormButtons.push({
                id: 'next-button',
                label: t('Avançar'),
                disabled: false,
                loading: false,
                title: t('UsuarioAdicaoEdicao'),
                ariaLabel: t('UsuarioAdicaoEdicao'),
                dataTestid: 'next-button',
                callback: goToReviewStepHandler,
            });
        }

        if (activeStep === UserFormSteps.review) {
            headerEditFormButtons.push({
                id: 'back-button',
                label: t('Voltar'),
                disabled: false,
                loading: false,
                title: t('UsuarioVoltarEtapaEdicao'),
                ariaLabel: t('UsuarioVoltarEtapaEdicao'),
                dataTestid: 'back-button',
                callback: goToPreviousStepHandler,
            });
            headerEditFormButtons.push({
                id: 'finish-button',
                label: isThirdUser()
                    ? t('"UsuarioCriarSolicitacao')
                    : t('Finalizar'),
                disabled: false,
                loading: false,
                title: t('UsuarioFinalizarEdicao'),
                ariaLabel: t('UsuarioFinalizarEdicao'),
                dataTestid: 'finish-button',
                callback: showUserEditFormConfirmationModal,
            });
        }

        return headerEditFormButtons;
    }, [
        activeStep,
        closeUserEditFormHandler,
        goToReviewStepHandler,
        goToPreviousStepHandler,
        showUserEditFormConfirmationModal,
        isThirdUser,
        t,
    ]);

    const submitUserUpdateData = useCallback(async () => {
        setIsShowingUserFormConfirmationModal(false);
        setIsLoading(true);

        let toastError = true;
        try {
            const response = await UserEditFormService.updateUser(
                userId,
                userData,
                t,
                isThirdUser(),
            );

            toastError = false;

            if (isThirdUser() && response.isApprovePending) {
                setToast(
                    'positive',
                    t('UsuarioAEdicaoEnviada'),
                    t('UsuarioAlteradoSucesso'),
                );
            } else {
                setToast('positive', t('Sucesso'));
            }
        } catch (e: any) {
            if (toastError) {
                isThirdUser()
                    ? setToast('warning', t('UsuarioErroEdicao'), e.message)
                    : setToast('negative', t('Erro'), e.message);
            }
        } finally {
            closeModalHandler();
            setIsLoading(false);
            setGetUsersData(true);
        }
    }, [
        closeModalHandler,
        setGetUsersData,
        setToast,
        userData,
        userId,
        isThirdUser,
        t,
    ]);

    const exitFormEditButton: ButtonPropsModal = {
        id: 'exit-button',
        label: t('Sair'),
        disabled: false,
        loading: false,
        title: t('UsuarioSairEdicao'),
        'data-testid': 'modal-exit-button',
        onClick: () => closeModalHandler(),
        role: 'button',
        size: 'md',
        styleType: 'danger',
    };

    const backStepButton: ButtonPropsModal = {
        id: 'back-button',
        label: t('Voltar'),
        disabled: false,
        loading: false,
        title: t('UsuarioVoltarEdicao'),
        'data-testid': 'modal-back-button',
        onClick: () => setIsExitModalOpen(false),
        role: 'button',
        size: 'md',
        styleType: 'secondary',
    };

    const buttonsModal: ItemsFooterType = [exitFormEditButton, backStepButton];

    const renderExitConfirmationUserFormModal = () => {
        if (isExitModalOpen) {
            return (
                <ClbModal
                    title={t('UsuarioCancelarEdicao')}
                    open={isExitModalOpen}
                    onClose={closeExitModalHandler}
                    description={t('UsuarioAoSairEdicao')}
                    type="danger"
                    itemsFooter={buttonsModal}
                />
            );
        }
    };

    const renderUserFormConfirmationModal = () => {
        if (isShowingUserFormConfirmationModal) {
            return (
                <ClbModal
                    title={t('UsuarioConfirmarEdicao')}
                    open
                    onClose={closeUserFormConfirmationModal}
                    description={t('UsuarioConfirmacaoEdicao')}
                    itemsFooter={[
                        {
                            id: 'edit-user-confirmation-modal-yes-button',
                            label: t('Sim'),
                            disabled: false,
                            loading: false,
                            title: t('UsuarioConfirmacaoAcaoEdicao'),
                            'data-testid':
                                'edit-user-confirmation-modal-yes-button',
                            onClick: async () => {
                                await submitUserUpdateData();
                            },
                            role: 'button',
                            size: 'md',
                        },
                        {
                            id: 'edit-user-confirmation-modal-no-button',
                            label: t('Não'),
                            disabled: false,
                            loading: false,
                            title: t('UsuarioRetornarEdicao'),
                            'data-testid':
                                'edit-user-confirmation-modal-no-button',
                            onClick: _ => closeUserFormConfirmationModal(),
                            role: 'button',
                            size: 'md',
                            styleType: 'secondary',
                        },
                    ]}
                />
            );
        }
    };

    const handleUserFormChange = useCallback(
        (handleUserFormChangeProps: IHandleUserFormChangeProps) => {
            const updatedUserData = {
                ...userData,
                [handleUserFormChangeProps.property]:
                    handleUserFormChangeProps.newValue,
            };

            if (
                !UserEditFormValidationService.checkIfInternalUser(
                    updatedUserData.email,
                )
            ) {
                revalidateInfos(updatedUserData);
            }

            setUserData(updatedUserData);
        },
        [userData, revalidateInfos],
    );

    return (
        <>
            <FullPageFlow
                title={pageTitle}
                type="progress"
                opened={isUserEditFormOpened}
                onClbClose={closeUserEditFormHandler}
                steps={1}
                buttonsList={headerButtonList}
                activeStep={activeStep}
                data-testid="full-page-flow"
            >
                {isLoading && <ClbLoading />}

                {isUserEditFormOpened &&
                    activeStep === UserFormSteps.info &&
                    !isLoading && (
                        <UserEditFormInfos
                            handleUserFormChange={handleUserFormChange}
                            userFormData={userData}
                            inputErrors={errors}
                            userAccountType={userAccountType}
                        />
                    )}

                {isUserEditFormOpened &&
                    activeStep === UserFormSteps.review &&
                    !isLoading && (
                        <UserOverview
                            userData={userData}
                            userAccountType={userAccountType}
                        />
                    )}
            </FullPageFlow>

            {renderExitConfirmationUserFormModal()}
            {renderUserFormConfirmationModal()}
        </>
    );
}

export default UserEditFormFullPageFlow;
